/**
 * Central RBAC permission catalogue — the single source of truth shared by the
 * API (guards + seed) and the web client (conditional UI).
 *
 * Format: `<resource>.<action>`. The backend ALWAYS enforces these; the client
 * only uses them to hide controls it would not be allowed to use anyway.
 */
export const PERMISSION_RESOURCES = [
    'organization',
    'members',
    'users',
    'roles',
    'teams',
    'team_members',
    'training',
    'season',
    'matches',
    'calendar',
    'notifications',
    'files',
    'audit',
    'import',
    'export',
    'equipment',
    'trainers',
];
export const PERMISSION_ACTIONS = [
    'view',
    'create',
    'edit',
    'delete',
    'manage',
    'export',
    'import',
];
/** Explicit catalogue. Only keys listed here are valid permissions. */
export const PERMISSIONS = [
    // organization
    { key: 'organization.view', description: 'Vereinsdaten und Einstellungen ansehen' },
    { key: 'organization.edit', description: 'Vereinsstammdaten und Einstellungen bearbeiten' },
    { key: 'organization.export', description: 'Vollständigen Vereins-Export erstellen' },
    { key: 'organization.import', description: 'Vereins-Import durchführen' },
    // members
    { key: 'members.view', description: 'Mitglieder ansehen' },
    { key: 'members.create', description: 'Mitglieder anlegen' },
    { key: 'members.edit', description: 'Mitglieder bearbeiten' },
    { key: 'members.delete', description: 'Mitglieder löschen' },
    { key: 'members.manage', description: 'Benutzerkonten, Rollen und Erziehungsberechtigte verwalten' },
    { key: 'members.export', description: 'Mitglieder exportieren' },
    { key: 'members.import', description: 'Mitglieder importieren' },
    // users & access
    { key: 'users.view', description: 'Benutzerkonten und Zuordnungen ansehen' },
    { key: 'users.manage', description: 'Benutzerkonten verwalten (einladen, sperren, zuordnen)' },
    { key: 'roles.view', description: 'Rollen und Rechte ansehen' },
    { key: 'roles.manage', description: 'Rollen, Rechte und Benutzer-Overrides verwalten' },
    // teams
    { key: 'teams.view', description: 'Mannschaften ansehen' },
    { key: 'teams.create', description: 'Mannschaften anlegen' },
    { key: 'teams.edit', description: 'Mannschaften bearbeiten' },
    { key: 'teams.delete', description: 'Mannschaften löschen' },
    { key: 'teams.sync', description: 'DBV / Turnier.de verbinden und Mannschaften synchronisieren' },
    { key: 'team_members.view', description: 'Kader / Mannschaftszuordnungen ansehen' },
    { key: 'team_members.manage', description: 'Mannschaftszuordnungen verwalten' },
    // training
    { key: 'training.view', description: 'Trainingstermine ansehen' },
    { key: 'training.create', description: 'Trainingstermine / -serien erstellen' },
    { key: 'training.edit', description: 'Trainingstermine bearbeiten' },
    { key: 'training.delete', description: 'Trainingstermine absagen / löschen' },
    { key: 'training.manage', description: 'Anwesenheit erfassen, Teilnehmer und Gruppen verwalten' },
    { key: 'training.respond', description: 'Auf Trainingstermine an-/abmelden (auch für verknüpfte Kinder)' },
    // season & matches
    { key: 'season.view', description: 'Saisons und Saisonspiele ansehen' },
    { key: 'season.edit', description: 'Saisons und Saisonspiele bearbeiten' },
    { key: 'matches.manage', description: 'Spielabstimmungen erstellen und verwalten' },
    { key: 'matches.respond', description: 'Auf Spielabstimmungen antworten' },
    // calendar
    {
        key: 'calendar.view',
        description: 'Kalender ansehen (Saisonspiele, Training und Vereinstermine zusammengeführt)',
    },
    { key: 'calendar.manage', description: 'Vereinstermine/-serien im Kalender anlegen, bearbeiten und löschen' },
    // notifications
    { key: 'notifications.view', description: 'Benachrichtigungen ansehen' },
    { key: 'notifications.manage', description: 'Benachrichtigungen versenden / konfigurieren' },
    // files
    { key: 'files.view', description: 'Dateien ansehen' },
    { key: 'files.manage', description: 'Dateien hochladen / löschen' },
    // audit
    { key: 'audit.view', description: 'Audit-Log ansehen' },
    // equipment
    { key: 'equipment.view', description: 'Ausrüstungs-Angebote ansehen' },
    { key: 'equipment.manage', description: 'Ausrüstungs-Angebote anlegen, bearbeiten und löschen' },
    // trainers (Trainer-Abrechnung)
    {
        key: 'trainers.manage',
        description: 'Trainer-Roster verwalten, Stundenauswertung ansehen/exportieren, fremde Eintragungen korrigieren',
    },
    {
        key: 'trainers.record',
        description: 'Sich selbst in Trainingseinheiten als Trainer eintragen — additiv auch über das Kennzeichen ' +
            '„Trainer" (members.is_trainer) nutzbar, nicht nur über diese Rolle',
    },
];
export const PERMISSION_KEYS = PERMISSIONS.map((p) => p.key);
export function isPermissionKey(value) {
    return PERMISSION_KEYS.includes(value);
}
/** System role templates cloned into every organization on creation. */
export const SYSTEM_ROLES = [
    {
        key: 'club_admin',
        name: 'Vereinsadministrator',
        permissions: PERMISSION_KEYS, // all
    },
    {
        key: 'team_manager',
        name: 'Mannschaftsverantwortlicher',
        permissions: [
            'organization.view',
            'teams.view',
            'teams.edit',
            'teams.sync',
            'team_members.view',
            'team_members.manage',
            'training.view',
            'training.respond',
            'season.view',
            'season.edit',
            'matches.manage',
            'calendar.view',
            'notifications.view',
            'notifications.manage',
            'equipment.view',
        ],
    },
    {
        key: 'coach',
        name: 'Trainer',
        permissions: [
            'organization.view',
            'teams.view',
            'team_members.view',
            'training.view',
            'training.create',
            'training.edit',
            'training.delete',
            'training.manage',
            'training.respond',
            'season.view',
            'calendar.view',
            'notifications.view',
            'equipment.view',
        ],
    },
    {
        key: 'assistant',
        name: 'Betreuer',
        permissions: [
            'organization.view',
            'teams.view',
            'team_members.view',
            'training.view',
            'training.manage',
            'season.view',
            'calendar.view',
            'notifications.view',
            'equipment.view',
        ],
    },
    {
        key: 'member',
        name: 'Mitglied',
        permissions: [
            'organization.view',
            'teams.view',
            'training.view',
            'training.respond',
            'season.view',
            'matches.respond',
            'calendar.view',
            'notifications.view',
            'equipment.view',
        ],
    },
    {
        key: 'parent',
        name: 'Elternteil',
        permissions: [
            'organization.view',
            'teams.view',
            'training.view',
            'training.respond',
            'season.view',
            'matches.respond',
            'calendar.view',
            'notifications.view',
            'equipment.view',
        ],
    },
    {
        key: 'read_only',
        name: 'Lesender Benutzer',
        permissions: [
            'organization.view',
            'teams.view',
            'team_members.view',
            'training.view',
            'season.view',
            'calendar.view',
            'notifications.view',
            'equipment.view',
        ],
    },
];
//# sourceMappingURL=permissions.js.map